/**
 * PersonaSwitcher — small "Viewing as: X ▾" pill rendered above the
 * mobile bottom nav (and inline in the desktop hamburger drawer
 * header) for users who can occupy more than one persona at once.
 *
 * Example: a breeder who's also a verified transporter. Their primary
 * resolves to `transporter` (precedence rule) but they still want
 * one-tap access to their breeder surface. This chip lets them flip.
 *
 * The override is persisted in `localStorage` under
 * `nav_persona_override` and read by `MobileBottomNav.jsx` to pick
 * the rendered preset. Clearing the override returns to the
 * data-resolved primary.
 */
import React, { useState } from "react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger,
} from "@/components/ui/sheet";
import { TruckIcon as Truck, HouseIcon as House, HeartStraightIcon as HeartStraight, StorefrontIcon as Storefront, ShieldStarIcon as ShieldStar, CaretDownIcon as CaretDown, CheckIcon as Check } from "@phosphor-icons/react";

const META = {
  transporter: { label: "Transporter", icon: Truck,         blurb: "Trips, bids & escrow" },
  breeder:     { label: "Breeder",     icon: House,         blurb: "Dogs, litters & sales" },
  dog_owner:   { label: "Dog Owner",   icon: HeartStraight, blurb: "Daily Dog Log + sitter share" },
  buyer:       { label: "Buyer",       icon: Storefront,    blurb: "Browse, watchlist & inquiries" },
  admin:       { label: "Admin",       icon: ShieldStar,    blurb: "Approvals & moderation" },
};

const STORAGE_KEY = "nav_persona_override";

export function readPersonaOverride() {
  try {
    return localStorage.getItem(STORAGE_KEY) || null;
  } catch {
    return null;
  }
}

export function writePersonaOverride(persona) {
  try {
    if (persona) localStorage.setItem(STORAGE_KEY, persona);
    else localStorage.removeItem(STORAGE_KEY);
    // Broadcast so other components (the bottom nav, the dashboard
    // greeting) update in the same tick.
    window.dispatchEvent(new CustomEvent("persona-override-changed", {
      detail: { persona },
    }));
  } catch { /* private mode */ }
}

/**
 * Renders the chip + sheet. `available` should come from `/me/nav-context`.
 * `current` is the persona currently rendered (could be override OR primary).
 * Hidden when the user only has one available persona.
 */
export default function PersonaSwitcher({ available = [], current, variant = "floating" }) {
  const [open, setOpen] = useState(false);
  if (!available || available.length < 2) return null;

  const meta = META[current] || META.breeder;
  const Icon = meta.icon;

  const trigger = variant === "floating" ? (
    <button
      type="button"
      data-testid="persona-switcher-trigger"
      className="md:hidden flex items-center gap-1.5 rounded-full bg-ink/90 backdrop-blur-md text-white pl-1 pr-3 py-2 min-h-[40px] text-[11px] font-bold uppercase tracking-[0.1em] shadow-[0_4px_12px_rgba(0,0,0,0.2)] active:scale-95 transition-all"
    >
      <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-gold text-ink">
        <Icon size={11} weight="fill" />
      </span>
      <span>{meta.label}</span>
      <CaretDown size={9} weight="bold" className="opacity-70" />
    </button>
  ) : (
    <button
      type="button"
      data-testid="persona-switcher-trigger-inline"
      className="w-full flex items-center justify-between gap-2 rounded-lg border border-[#E5E5E5] bg-white hover:bg-[#FAFAFA] px-3 py-2 text-[12px] font-semibold text-ink transition-colors"
    >
      <span className="flex items-center gap-2">
        <Icon size={14} weight="duotone" className="text-gold-dark" />
        Viewing as: {meta.label}
      </span>
      <CaretDown size={10} weight="bold" className="text-[#6B6B6B]" />
    </button>
  );

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>{trigger}</SheetTrigger>
      <SheetContent
        side="bottom"
        className="rounded-t-2xl border-t border-[#E5E5E5] p-0"
        data-testid="persona-switcher-sheet"
      >
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-[#E5E5E5]/70">
          <SheetTitle className="font-spectral text-2xl tracking-tight text-left">
            Switch view
          </SheetTitle>
          <SheetDescription className="text-left text-[#6B6B6B] text-sm">
            You wear multiple hats. Pick which surface you want one tap away — you can switch back anytime.
          </SheetDescription>
        </SheetHeader>
        <div className="px-3 py-3 space-y-1">
          {available.map((p) => {
            const m = META[p];
            if (!m) return null;
            const PIcon = m.icon;
            const isCurrent = p === current;
            return (
              <button
                key={p}
                type="button"
                onClick={() => {
                  writePersonaOverride(isCurrent ? null : p);
                  setOpen(false);
                }}
                data-testid={`persona-switcher-${p}`}
                className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl border text-left transition-colors ${
                  isCurrent
                    ? "border-gold/40 bg-[#FBF7EC]"
                    : "border-[#E5E5E5] bg-white hover:bg-[#FAFAFA]"
                }`}
              >
                <span
                  className={`inline-flex items-center justify-center h-10 w-10 rounded-full flex-shrink-0 ${
                    isCurrent ? "bg-gold text-ink" : "bg-[#FBF7EC] text-gold-dark border border-[#E8D9A6]"
                  }`}
                >
                  <PIcon size={18} weight={isCurrent ? "fill" : "duotone"} />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[14px] font-semibold text-ink">{m.label}</span>
                  <span className="block text-[11px] text-[#6B6B6B]">{m.blurb}</span>
                </span>
                {isCurrent && (
                  <Check size={16} weight="bold" className="text-gold-dark flex-shrink-0" />
                )}
              </button>
            );
          })}
        </div>
        <div className="px-5 pb-5 pt-2">
          <p className="text-[11px] text-[#6B6B6B] leading-relaxed">
            Your "view" only changes which tabs appear in the bottom nav and how the dashboard greets you. All of your data stays exactly where it is.
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
}
